/**
 * Pill-shaped toggle button with small/large sizes and selected, disabled, and
 * loading states.
 *
 * Built on React Aria's ToggleButton, so selected state is published as native
 * `aria-pressed` (and `[data-selected]` for styling) rather than the
 * role='button' + aria-label suffix the previous React Native version faked.
 * Backgrounds follow the Figma design system: white when unselected, a subtle
 * surface when small + selected, and the near-black primary fill with white
 * content when large + selected. The loading state mirrors CTAButton: hide the
 * content, overlay a centered ProgressCircle, set the cursor to default, and —
 * because RAC's ToggleButton has no `isPending` — block presses via
 * `isDisabled`, preventing the indeterminate-final-value problem from rapid
 * repeated clicks. On the dark large-selected fill the spinner arc switches to
 * the on-dark accent so it stays visible.
 */
import { type CarbonIconType, CheckmarkFilled, CheckmarkOutline } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { cva } from 'class-variance-authority';
import { UtilityText } from 'component/text';
import { ProgressCircle } from 'component/ui/progress-circle';
import { composeRenderProps, ToggleButton as RACToggleButton, type ToggleButtonProps as RACToggleButtonProps } from 'react-aria-components';
import { makeTestID } from 'util/stdtypes';

import styles from './toggle-button.module.css';

const button = cva(styles.button, {
    variants: {
        size: { small: styles.small, large: styles.large },
        loading: { true: styles.loading },
    },
    defaultVariants: { size: 'small' },
});

/** @public */
export type ToggleButtonProps = Omit<RACToggleButtonProps, 'children'> & {
    emoji?: string;
    text: string;
    /** Pill height/type: small (32px) or large (40px). Defaults to small. */
    size?: 'small' | 'large';
    /** Trailing icon when unselected; selected shows a filled checkmark. */
    icon?: CarbonIconType;
    /** When true, blocks presses and overlays a {@link ProgressCircle}. */
    isLoading?: boolean;
    /** Accessible label for the spinner. Defaults to "Loading". */
    loadingLabel?: string;
    testID?: string;
};

export function ToggleButton({
    emoji,
    text,
    size = 'small',
    icon: OffIcon = CheckmarkOutline,
    isLoading,
    loadingLabel,
    testID,
    isDisabled,
    className,
    ...rest
}: ToggleButtonProps) {
    const { colors } = useTheme();
    const isLarge = size === 'large';
    const iconSize = isLarge ? 20 : 16;
    // composeRenderProps: see Dev Notes 'Extending wrapper styles'.
    const composedClassName = composeRenderProps(className, (callerClass) => button({ size, loading: isLoading, className: callerClass }));
    return (
        <RACToggleButton
            {...rest}
            className={composedClassName}
            data-testid={makeTestID({ testID, text })}
            isDisabled={isLoading || isDisabled}
        >
            {({ isSelected }) => {
                // Large + selected is the dark fill; content goes light.
                const isDark = isLarge && isSelected;
                const isGenuinelyDisabled = !!isDisabled && !isLoading;
                const contentColor = isGenuinelyDisabled
                    ? colors.colorTextDisabled
                    : isDark
                      ? colors.colorTextOnPrimaryButton
                      : colors.colorTextPrimary;
                // Per Figma: unselected icon is grey on small, black on large.
                const offIconColor = isGenuinelyDisabled
                    ? colors.colorTextDisabled
                    : isLarge
                      ? colors.colorTextPrimary
                      : colors.colorBorderPrimary;
                return (
                    <>
                        <span className={isLoading ? styles.invisible : styles.content}>
                            {emoji && (
                                <UtilityText
                                    aria-hidden
                                    text={emoji}
                                    type={isLarge ? 'large' : 'small'}
                                    weight={isLarge ? 'regular' : 'medium'}
                                />
                            )}
                            <UtilityText
                                color={contentColor}
                                text={text}
                                type={isLarge ? 'large' : 'small'}
                                weight={isLarge ? 'regular' : 'medium'}
                            />
                            {isSelected ? (
                                <CheckmarkFilled color={contentColor} size={iconSize} />
                            ) : (
                                <OffIcon color={offIconColor} size={iconSize} />
                            )}
                        </span>
                        {isLoading && (
                            <span className={styles.spinnerWrapper}>
                                <ProgressCircle
                                    arcColor={isDark ? 'var(--colorBorderAccentOnDarkBg)' : 'var(--colorBorderAccent)'}
                                    aria-label={loadingLabel ?? 'Loading'}
                                />
                            </span>
                        )}
                    </>
                );
            }}
        </RACToggleButton>
    );
}
